import React, { useEffect, useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import UserPicker from '../../../components/Common/UserPicker';
import UserMultiPicker from '../../../components/Common/UserMultiPicker';
import taskService, { PickableUser } from '../../../services/taskService';
import { commonService } from '../../../services/commonService';
import referenceFileService from '../../../services/referenceFileService';
import { getMyUserId, isCXUser } from '../../../utils/permissions';
import '../../../assets/styles/Workflow.css';

const STEPS = ['Project Information', 'Requirements', 'Delivery Requirements', 'Review & Submit'];

// The 15 fields describing one audience's data requirements - shared shape
// between a plain CriteriaGroup's own flat fields and, for
// AUDIENCE_SPLIT_BRANDS only, its nested attendees/exhibitors sub-briefs.
export interface AudienceCriteria {
  primaryIndustry: string;
  secondaryIndustry: string;
  naicsCode: string;
  sicCode: string;
  companyKeywords: string;
  department: string;
  employeeSize: string;
  revenueSize: string;
  geography: string;
  technologyUsed: string;
  jobTitles: string;
  specialty: string;
  numberOfCompanies: string;
  numberOfContacts: string;
  dataFields: string;
}

const blankAudienceCriteria = (): AudienceCriteria => ({
  primaryIndustry: '', secondaryIndustry: '', naicsCode: '', sicCode: '', companyKeywords: '',
  department: '', employeeSize: '', revenueSize: '', geography: '', technologyUsed: '',
  jobTitles: '', specialty: '', numberOfCompanies: '', numberOfContacts: '', dataFields: '',
});

// Field metadata driving the read-only displays (Step 4 review here, and
// ProjectDetailOverlay's criteria card/copy) so they can't drift from each
// other as fields are added - the editable popup form below is hand-written
// since its inputs have per-field behavior (digit-stripping, required flags,
// grid pairing) a data-driven table would need to re-encode anyway.
export const CRITERIA_FIELD_LIST: { key: keyof AudienceCriteria; label: string }[] = [
  { key: 'primaryIndustry', label: 'Primary Industry' },
  { key: 'secondaryIndustry', label: 'Secondary Industry' },
  { key: 'naicsCode', label: 'NAICS Code' },
  { key: 'sicCode', label: 'SIC Code' },
  { key: 'companyKeywords', label: 'Company Keywords' },
  { key: 'department', label: 'Department' },
  { key: 'employeeSize', label: 'Employee Size' },
  { key: 'revenueSize', label: 'Revenue Size' },
  { key: 'geography', label: 'Geography' },
  { key: 'technologyUsed', label: 'Technology Used' },
  { key: 'jobTitles', label: 'Job Titles' },
  { key: 'specialty', label: 'Specialty' },
  { key: 'numberOfCompanies', label: 'No. of Companies' },
  { key: 'numberOfContacts', label: 'No. of Contacts' },
  { key: 'dataFields', label: 'Data Fields' },
];

// One audience/requirements brief. A project can carry more than one of
// these (e.g. two different audience segments on the same project, each
// with its own industry/company/contact details and its own BD notes).
//
// includeAttendees/includeExhibitors/attendees/exhibitors only apply to
// AUDIENCE_SPLIT_BRANDS (VisitorsList/ExpoCaptive) - trade-show data where
// Attendee and Exhibitor contacts need independently specified criteria.
// Every other brand leaves these untouched (both checkboxes false, both
// sub-objects blank) and behaves exactly as before this feature existed.
interface CriteriaGroup extends AudienceCriteria {
  bdAdditionalNotes: string;
  includeAttendees: boolean;
  includeExhibitors: boolean;
  attendees: AudienceCriteria;
  exhibitors: AudienceCriteria;
}

const blankCriteriaGroup = (): CriteriaGroup => ({
  ...blankAudienceCriteria(),
  bdAdditionalNotes: '',
  includeAttendees: false,
  includeExhibitors: false,
  attendees: blankAudienceCriteria(),
  exhibitors: blankAudienceCriteria(),
});

// VisitorsList/ExpoCaptive only - see CriteriaGroup's docstring above.
// Exported so other FDC display surfaces (e.g. ProjectDetailOverlay) use the
// exact same brand list instead of risking a second, independently-drifting
// copy of it.
export const AUDIENCE_SPLIT_BRANDS = ['VisitorsList', 'ExpoCaptive'];

export const audienceCriteriaFromApi = (a: any): AudienceCriteria => ({
  // industry/naics_sic_code fallbacks: only relevant when this is called on
  // an old flat (pre-split) group as part of the legacy-migration fallback
  // in draftFromItem below - genuine attendees/exhibitors sub-objects never
  // carry these pre-restructure keys, so the fallback is a harmless no-op there.
  primaryIndustry: a?.primary_industry || a?.industry || '',
  secondaryIndustry: a?.secondary_industry || '',
  naicsCode: a?.naics_code || a?.naics_sic_code || '',
  sicCode: a?.sic_code || '',
  companyKeywords: a?.company_keywords || '',
  department: a?.department || '',
  employeeSize: a?.company_size || '',
  revenueSize: a?.revenue_size || '',
  geography: a?.geography || '',
  technologyUsed: a?.technology_used || '',
  jobTitles: a?.job_title || '',
  specialty: a?.specialty || '',
  numberOfCompanies: a?.number_of_companies || '',
  numberOfContacts: a?.number_of_contacts || '',
  dataFields: a?.data_fields || '',
});

const audienceCriteriaToApi = (a: AudienceCriteria) => ({
  primary_industry: a.primaryIndustry,
  secondary_industry: a.secondaryIndustry || '',
  naics_code: a.naicsCode || '',
  sic_code: a.sicCode || '',
  company_keywords: a.companyKeywords || '',
  department: a.department,
  company_size: a.employeeSize,
  revenue_size: a.revenueSize,
  geography: a.geography,
  technology_used: a.technologyUsed || '',
  job_title: a.jobTitles || '',
  specialty: a.specialty,
  number_of_companies: a.numberOfCompanies,
  number_of_contacts: a.numberOfContacts,
  data_fields: a.dataFields || '',
});

// Total contacts for one group - attendees+exhibitors for the split brands
// (matching the backend's own sum), the flat field for every other brand.
// Shared by the Step 2 summary badge, the Step 4 review, and the submit
// payload builder below so the 3 can never disagree.
const groupContactCount = (g: CriteriaGroup, brand: string): number => {
  if (AUDIENCE_SPLIT_BRANDS.includes(brand)) {
    const attendees = g.includeAttendees ? (parseInt(g.attendees.numberOfContacts, 10) || 0) : 0;
    const exhibitors = g.includeExhibitors ? (parseInt(g.exhibitors.numberOfContacts, 10) || 0) : 0;
    return attendees + exhibitors;
  }
  return parseInt(g.numberOfContacts, 10) || 0;
};

// Maps one draft criteria group to the snake_case shape /fdc/create and
// /fdc/update expect. For AUDIENCE_SPLIT_BRANDS, the top-level
// number_of_contacts is still set (to the attendees+exhibitors total) even
// though it's superseded by the nested per-audience counts, because the
// backend schema keeps that field required - see CriteriaGroup's backend
// docstring.
const criteriaGroupToApi = (g: CriteriaGroup, brand: string) => {
  if (!AUDIENCE_SPLIT_BRANDS.includes(brand)) {
    return {
      primary_industry: g.primaryIndustry,
      secondary_industry: g.secondaryIndustry || '',
      naics_code: g.naicsCode || '',
      sic_code: g.sicCode || '',
      company_keywords: g.companyKeywords || '',
      department: g.department,
      company_size: g.employeeSize,
      revenue_size: g.revenueSize,
      geography: g.geography,
      technology_used: g.technologyUsed || '',
      job_title: g.jobTitles || '',
      specialty: g.specialty,
      number_of_companies: g.numberOfCompanies,
      number_of_contacts: g.numberOfContacts,
      data_fields: g.dataFields || '',
      bd_additional_notes: g.bdAdditionalNotes || '',
    };
  }
  return {
    bd_additional_notes: g.bdAdditionalNotes || '',
    number_of_contacts: String(groupContactCount(g, brand)),
    ...(g.includeAttendees ? { attendees: audienceCriteriaToApi(g.attendees) } : {}),
    ...(g.includeExhibitors ? { exhibitors: audienceCriteriaToApi(g.exhibitors) } : {}),
  };
};

interface Draft {
  summary: string;
  crmId: string;
  dealId: string;
  acquisitionType: 'fulfillment' | 'refulfillment' | 'goodwill' | 'credit_fulfillment';
  listTypes: string[];
  projectTier: string;
  paymentType: string;
  dueDate: string;
  brand: string;
  assigneeId: number | null;
  refulfillmentId: string;
  criteriaGroups: CriteriaGroup[];
  comment: string;
  participantIds: number[];
  observerIds: number[];
}

const blankDraft = (): Draft => ({
  summary: '', crmId: '', dealId: '', acquisitionType: 'fulfillment', listTypes: ['B2B'],
  projectTier: 'T1', paymentType: '', dueDate: '', brand: '', assigneeId: null, refulfillmentId: '',
  criteriaGroups: [blankCriteriaGroup()],
  comment: '',
  participantIds: [], observerIds: [],
});

// task_deadline_date and assignee_id are both supported on /fdc/update, but
// BD-owner/admin only - see isBdOwnerOrAdmin.
const draftFromItem = (item: any): Draft => ({
  summary: item.summary || '',
  crmId: item.crm_id != null ? String(item.crm_id) : '',
  dealId: item.deal_id || '',
  acquisitionType: ['refulfillment', 'goodwill', 'credit_fulfillment'].includes(item.record_type) ? item.record_type : 'fulfillment',
  listTypes: (item.criteria || 'B2B').split(',').map((s: string) => s.trim()).filter(Boolean),
  projectTier: item.project_tire || 'T1',
  paymentType: item.payment_type || '',
  dueDate: item.task_deadline_date ? String(item.task_deadline_date).slice(0, 10) : '',
  brand: item.brand_name || '',
  assigneeId: item.assignee_id || null,
  refulfillmentId: item.refulfillment_id || '',
  // Older projects predate criteria_groups (backfilled server-side into a
  // 1-item array from their old single other/number_of_contacts/data_fields
  // values) - falling back here too in case an older cached item slips
  // through. Also falls back per-field to the pre-restructure key names
  // (primary_industry didn't exist until the Industry/Department split -
  // those rows still carry the old single `industry` key; `requirements`
  // similarly predates `bd_additional_notes`) so editing an old project
  // doesn't show blank fields for data that's actually still there.
  criteriaGroups: Array.isArray(item.criteria_groups) && item.criteria_groups.length > 0
    ? item.criteria_groups.map((g: any) => {
        // A VisitorsList/ExpoCaptive group saved before this feature existed
        // has neither key - rather than hide that data behind an empty
        // Attendees/Exhibitors UI with no way to see or save it, treat it as
        // an Attendees brief by default (checkbox pre-checked, fields
        // pre-filled from the old flat data). The user sees their existing
        // data immediately and can save without re-entering anything; the
        // next save persists it in the current nested shape. Brand-agnostic
        // here since it's a no-op for brands that never populate either key.
        const legacyFlat = !g.attendees && !g.exhibitors;
        return {
          primaryIndustry: g.primary_industry || g.industry || '',
          secondaryIndustry: g.secondary_industry || '',
          naicsCode: g.naics_code || g.naics_sic_code || '',
          sicCode: g.sic_code || '',
          companyKeywords: g.company_keywords || '',
          department: g.department || '',
          employeeSize: g.company_size || '',
          revenueSize: g.revenue_size || '',
          geography: g.geography || '',
          technologyUsed: g.technology_used || '',
          jobTitles: g.job_title || '',
          specialty: g.specialty || '',
          numberOfCompanies: g.number_of_companies || '',
          numberOfContacts: g.number_of_contacts || '',
          dataFields: g.data_fields || '',
          bdAdditionalNotes: g.bd_additional_notes || g.requirements || '',
          includeAttendees: legacyFlat || !!g.attendees,
          includeExhibitors: !!g.exhibitors,
          attendees: legacyFlat ? audienceCriteriaFromApi(g) : audienceCriteriaFromApi(g.attendees),
          exhibitors: audienceCriteriaFromApi(g.exhibitors),
        };
      })
    : [{
        ...blankCriteriaGroup(),
        bdAdditionalNotes: item.other || '',
        numberOfContacts: item.number_of_contacts || '',
        dataFields: item.data_fields || '',
      }],
  comment: item.comment || '',
  participantIds: [], observerIds: [],
});

// BDA name for the generated project title - read from the same localStorage
// "user" blob Navbar already populates at login (first_name/last_name), no
// extra API call needed.
const getBdaName = (): string => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return '';
    const u = JSON.parse(raw);
    return [u?.first_name, u?.last_name].filter(Boolean).join(' ').trim();
  } catch {
    return '';
  }
};

// Only these 3 brands are active - the rest are commented out (not
// deleted) rather than removed outright, since a handful of older projects
// still carry one of them and this list is shared by the edit wizard too.
const BRANDS = [
  'DataCaptive',
  // 'ContactAffix',
  // 'ProDataLabs',
  'VisitorsList',
  // 'TechDataPark',
  // 'CampaignLake',
  // 'ESalesClub',
  // 'InboxCEO',
  // 'MedicoLeads',
  'ExpoCaptive',
];

const Field: React.FC<{ label: string; children: React.ReactNode; hint?: string; required?: boolean }> = ({ label, children, hint, required }) => (
  <div>
    <label className="wf-field-label">
      {label}
      {required && <span style={{ color: '#ef4444' }}> *</span>}
    </label>
    {children}
    {hint && <div className="wf-field-hint">{hint}</div>}
  </div>
);

// Read-only label/value grid for one audience's criteria - used by the
// Step 4 review below, and by ProjectDetailOverlay's criteria display.
export const CriteriaReadOnlyGrid: React.FC<{ values: AudienceCriteria; labelPrefix?: string }> = ({ values, labelPrefix }) => (
  <div className="wf-criteria-detail-grid" style={{ borderTop: 'none', paddingTop: 0, marginTop: 4 }}>
    {CRITERIA_FIELD_LIST.map(({ key, label }) => (
      <React.Fragment key={key}>
        <span className="wf-k">{labelPrefix ? `${labelPrefix} ${label}` : label}</span>
        <span>{values[key] || '—'}</span>
      </React.Fragment>
    ))}
  </div>
);

// The editable Industry/Company/Contact/Data Volume fields (everything
// except BD Additional Notes, which stays shared/unscoped per group) - a
// pure extraction of what used to be inline in the criteria popup below, so
// an AUDIENCE_SPLIT_BRANDS group can render this twice (once per audience,
// each field labeled with that audience's name) while every other brand's
// single rendering stays identical to before this feature existed.
const CriteriaFieldsBlock: React.FC<{
  values: AudienceCriteria;
  onChange: (patch: Partial<AudienceCriteria>) => void;
  labelPrefix?: string;
}> = ({ values, onChange, labelPrefix }) => {
  const lbl = (base: string) => (labelPrefix ? `${labelPrefix} ${base}` : base);
  return (
    <div className="wf-stack">
      <div className="wf-field-label" style={{ fontSize: 13, color: '#374151' }}>Industry Details</div>
      <div className="wf-grid2">
        <Field label={lbl('Primary Industry')} required>
          <input
            type="text"
            value={values.primaryIndustry}
            onChange={(e) => onChange({ primaryIndustry: e.target.value })}
            placeholder="Enter primary industry"
          />
        </Field>
        <Field label={lbl('Secondary Industry')}>
          <input
            type="text"
            value={values.secondaryIndustry}
            onChange={(e) => onChange({ secondaryIndustry: e.target.value })}
            placeholder="Enter secondary industry"
          />
        </Field>
      </div>
      <div className="wf-grid2">
        <Field label={lbl('NAICS Code')}>
          <input
            type="text"
            value={values.naicsCode}
            onChange={(e) => onChange({ naicsCode: e.target.value })}
            placeholder="Enter NAICS code"
          />
        </Field>
        <Field label={lbl('SIC Code')}>
          <input
            type="text"
            value={values.sicCode}
            onChange={(e) => onChange({ sicCode: e.target.value })}
            placeholder="Enter SIC code"
          />
        </Field>
      </div>

      <div className="wf-field-label" style={{ fontSize: 13, color: '#374151', marginTop: 6 }}>Company Details</div>
      <Field label={lbl('Company Keywords')}>
        <input
          type="text"
          value={values.companyKeywords}
          onChange={(e) => onChange({ companyKeywords: e.target.value })}
          placeholder="Enter company keywords"
        />
      </Field>
      <Field label={lbl('Department')} required>
        <input
          type="text"
          value={values.department}
          onChange={(e) => onChange({ department: e.target.value })}
          placeholder="Enter department"
        />
      </Field>
      <div className="wf-grid2">
        <Field label={lbl('Employee Size')} required>
          <input
            type="text"
            value={values.employeeSize}
            onChange={(e) => onChange({ employeeSize: e.target.value })}
            placeholder="Enter employee size"
          />
        </Field>
        <Field label={lbl('Revenue Size')} required>
          <input
            type="text"
            value={values.revenueSize}
            onChange={(e) => onChange({ revenueSize: e.target.value })}
            placeholder="Enter revenue size"
          />
        </Field>
      </div>
      <div className="wf-grid2">
        <Field label={lbl('Geography')} required>
          <input
            type="text"
            value={values.geography}
            onChange={(e) => onChange({ geography: e.target.value })}
            placeholder="Enter geography"
          />
        </Field>
        <Field label={lbl('Technology Used')}>
          <input
            type="text"
            value={values.technologyUsed}
            onChange={(e) => onChange({ technologyUsed: e.target.value })}
            placeholder="Enter technology used"
          />
        </Field>
      </div>

      <div className="wf-field-label" style={{ fontSize: 13, color: '#374151', marginTop: 6 }}>Contact Details</div>
      <div className="wf-grid2">
        <Field label={lbl('Job Titles')}>
          <input
            type="text"
            value={values.jobTitles}
            onChange={(e) => onChange({ jobTitles: e.target.value })}
            placeholder="Enter job titles"
          />
        </Field>
        <Field label={lbl('Specialty')} required>
          <input
            type="text"
            value={values.specialty}
            onChange={(e) => onChange({ specialty: e.target.value })}
            placeholder="Enter specialty"
          />
        </Field>
      </div>

      <div className="wf-field-label" style={{ fontSize: 13, color: '#374151', marginTop: 6 }}>Data Volume</div>
      <div className="wf-grid2">
        <Field label={lbl('No. of Companies')} required>
          <input
            type="text"
            value={values.numberOfCompanies}
            onChange={(e) => onChange({ numberOfCompanies: e.target.value.replace(/[^0-9]/g, '') })}
            placeholder="Enter number of companies"
          />
        </Field>
        <Field label={lbl('No. of Contacts')} required>
          <input
            type="text"
            value={values.numberOfContacts}
            onChange={(e) => onChange({ numberOfContacts: e.target.value.replace(/[^0-9]/g, '') })}
            placeholder="Enter number of contacts"
          />
        </Field>
      </div>
      <Field label={lbl('Data Fields')}>
        <input
          type="text"
          value={values.dataFields}
          onChange={(e) => onChange({ dataFields: e.target.value })}
          placeholder="Enter data fields"
        />
      </Field>
    </div>
  );
};

interface FDCCreateWizardProps {
  onClose: () => void;
  onCreated?: () => void;
  /** When provided, the wizard edits this existing project instead of creating a new one. */
  editItem?: any;
}

const FDCCreateWizard: React.FC<FDCCreateWizardProps> = ({ onClose, onCreated, editItem }) => {
  const isEdit = !!editItem;
  const [step, setStep] = useState(1);
  const [d, setD] = useState<Draft>(() => (editItem ? draftFromItem(editItem) : blankDraft()));
  const [refulfillOptions, setRefulfillOptions] = useState<{ projectid: string; summary: string }[]>([]);
  const [readOnlyObservers, setReadOnlyObservers] = useState<{ user_id: number; name: string | null }[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Staged client-side until the project itself is created/saved - any type
  // of file (sample/enrichment/work file, anything), uploaded to the new
  // "Reference / Files" section right after a successful submit. That
  // section (in ProjectDetailOverlay) is where they're listed/downloaded/
  // deleted afterward - this wizard is upload-only.
  const [pendingReferenceFiles, setPendingReferenceFiles] = useState<File[]>([]);
  const todayStr = new Date().toISOString().split('T')[0];

  // UserPicker only remembers who's selected via its own internal state, which
  // resets to blank on remount - stepping to step 2 and back to step 1
  // unmounts/remounts this field, so without this the assignee picker showed
  // empty even though d.assigneeId (the actual draft value) was still correct.
  const [assigneeUser, setAssigneeUser] = useState<PickableUser | null>(null);
  useEffect(() => {
    if (!d.assigneeId) { setAssigneeUser(null); return; }
    let active = true;
    taskService.getUsersByIds([d.assigneeId]).then((users) => {
      if (active) setAssigneeUser(users[0] || null);
    }).catch(() => { if (active) setAssigneeUser(null); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d.assigneeId]);

  // Observer management mirrors ProjectDetailOverlay's canManageObservers -
  // admins and the project's creating BD user only; unrestricted at creation
  // time since only BD/admin can reach this wizard's create mode at all.
  const isAdmin = localStorage.getItem('role_id') === '1';
  const isBD = localStorage.getItem('role_id') === '8';
  const canManageObservers = !isEdit || isAdmin || (isBD && editItem?.created_by_id === getMyUserId());
  // Same ownership rule as observers - the BD who created the project (or
  // admin) is the only one who can move the due date or hand it off to a
  // different PC Manager once it exists; unrestricted at creation time.
  const isBdOwnerOrAdmin = !isEdit || isAdmin || (isBD && editItem?.created_by_id === getMyUserId());

  useEffect(() => {
    if ((d.acquisitionType === 'refulfillment' || d.acquisitionType === 'credit_fulfillment') && d.crmId.trim()) {
      const timer = setTimeout(async () => {
        try {
          const res = await commonService.getProjectId({ crm_id: d.crmId });
          setRefulfillOptions(res?.message === 'success' ? (res?.data || []) : []);
        } catch {
          setRefulfillOptions([]);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [d.crmId, d.acquisitionType]);

  // Prefill the pickers with this project's existing creation-time members -
  // draftFromItem can't do this since it's synchronous and editItem doesn't
  // carry participant/observer data.
  useEffect(() => {
    if (!isEdit || !editItem) return;
    let active = true;
    taskService.getTasksByProject(editItem.id).then((res) => {
      if (!active) return;
      const members = res.project_members || [];
      const observers = members.filter((m) => m.member_type === 'observer');
      update({
        participantIds: members.filter((m) => m.member_type === 'participant').map((m) => m.user_id),
        observerIds: observers.map((m) => m.user_id),
      });
      setReadOnlyObservers(observers);
    }).catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, editItem?.id]);

  // Pre-fills Participants with the standing default-participant list, but
  // only for a brand NEW project - editing instead re-derives participants
  // from what's actually already on the project (the effect above). Only
  // applies if nothing's been picked yet, so it can't clobber a selection
  // the BDA already made in the brief window before this resolves.
  useEffect(() => {
    if (isEdit) return;
    let active = true;
    commonService.fdcDefaultParticipants().then((users) => {
      if (!active) return;
      setD((prev) => (prev.participantIds.length === 0
        ? { ...prev, participantIds: users.map((u) => u.id) }
        : prev));
    }).catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  const update = (patch: Partial<Draft>) => setD((prev) => ({ ...prev, ...patch }));

  // Live-generated title - "NLA - Fulfillment/Re-Fulfillment - Contact ID -
  // BDA name - CC/Non-CC - Tier - Brand - B2B/B2C - Deal ID". Total contacts
  // and due date are deliberately NOT part of this string - they're shown as
  // their own line below the title instead (ProjectDetailOverlay), so the
  // title stays a stable identifier instead of churning every time either of
  // those genuinely-changeable facts is edited. Regenerated on every other
  // relevant field change in BOTH create and edit mode (explicit instruction
  // - a project's title should always reflect its current state, not freeze
  // at whatever it said when the project was first created). The BDA-name
  // segment always uses the project's original creator (editItem.created_by_name
  // in edit mode), never the person currently doing the editing - a PC
  // Manager or admin editing someone else's project must not overwrite that
  // name with their own.
  useEffect(() => {
    const bdaName = isEdit ? (editItem?.created_by_name || getBdaName()) : getBdaName();
    const fulfillmentLabel = d.acquisitionType === 'refulfillment' ? 'Re-Fulfillment' : d.acquisitionType === 'goodwill' ? 'Goodwill' : d.acquisitionType === 'credit_fulfillment' ? 'Credit Fulfillment' : 'Fulfillment';
    const listTypeLabel = d.listTypes.includes('B2B') && d.listTypes.includes('B2C')
      ? 'B2B+B2C'
      : (d.listTypes.includes('B2B') ? 'B2B' : d.listTypes.includes('B2C') ? 'B2C' : '');
    const generated = [
      'NLA', fulfillmentLabel, d.crmId, bdaName, d.paymentType, d.projectTier, d.brand, listTypeLabel, d.dealId,
    ].filter(Boolean).join(' - ');
    if (generated !== d.summary) update({ summary: generated });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, editItem?.created_by_name, d.acquisitionType, d.crmId, d.paymentType, d.projectTier, d.brand, d.listTypes, d.dealId, d.summary]);

  const removeCriteriaGroup = (index: number) => setD((prev) => (
    prev.criteriaGroups.length === 1 ? prev : { ...prev, criteriaGroups: prev.criteriaGroups.filter((_, i) => i !== index) }
  ));

  // Criteria groups are edited in their own popup, stacked on top of this
  // wizard's own modal - `index: null` means "adding a new one" (the draft
  // is only appended to d.criteriaGroups on Save; Cancel discards it clean,
  // never leaving a stray blank entry behind the way editing it inline used to).
  const [criteriaPopup, setCriteriaPopup] = useState<{ index: number | null; draft: CriteriaGroup } | null>(null);
  const openAddCriteria = () => setCriteriaPopup({ index: null, draft: blankCriteriaGroup() });
  const openEditCriteria = (index: number) => setCriteriaPopup({ index, draft: { ...d.criteriaGroups[index] } });
  const closeCriteriaPopup = () => setCriteriaPopup(null);
  const updateCriteriaPopupDraft = (patch: Partial<CriteriaGroup>) => setCriteriaPopup((prev) => (prev ? { ...prev, draft: { ...prev.draft, ...patch } } : prev));
  const isAudienceCriteriaComplete = (c: AudienceCriteria) => (
    !!c.primaryIndustry.trim() && !!c.department.trim() && !!c.employeeSize.trim() && !!c.revenueSize.trim() && !!c.geography.trim()
    && !!c.specialty.trim() && !!c.numberOfCompanies.trim() && !!c.numberOfContacts.trim()
  );
  const isCriteriaGroupComplete = (g: CriteriaGroup, brand: string) => {
    if (AUDIENCE_SPLIT_BRANDS.includes(brand)) {
      if (!g.includeAttendees && !g.includeExhibitors) return false;
      if (g.includeAttendees && !isAudienceCriteriaComplete(g.attendees)) return false;
      if (g.includeExhibitors && !isAudienceCriteriaComplete(g.exhibitors)) return false;
      return !!g.bdAdditionalNotes.trim();
    }
    return isAudienceCriteriaComplete(g) && !!g.bdAdditionalNotes.trim();
  };
  const criteriaPopupValid = !!criteriaPopup && isCriteriaGroupComplete(criteriaPopup.draft, d.brand);
  const saveCriteriaPopup = () => {
    if (!criteriaPopup || !criteriaPopupValid) return;
    const { index, draft } = criteriaPopup;
    setD((prev) => ({
      ...prev,
      criteriaGroups: index === null
        ? [...prev.criteriaGroups, draft]
        : prev.criteriaGroups.map((g, i) => (i === index ? draft : g)),
    }));
    setCriteriaPopup(null);
  };

  const toggleListType = (type: string) => {
    setD((prev) => {
      const has = prev.listTypes.includes(type);
      if (has) {
        if (prev.listTypes.length === 1) return prev; // keep at least one selected
        return { ...prev, listTypes: prev.listTypes.filter((t) => t !== type) };
      }
      return { ...prev, listTypes: [...prev.listTypes, type] };
    });
  };

  const stepOneValid = !!(d.summary && d.crmId && d.dealId && d.projectTier && d.brand && (isEdit || (d.assigneeId && d.paymentType)));
  const stepTwoValid = d.criteriaGroups.length > 0 && d.criteriaGroups.every((g) => isCriteriaGroupComplete(g, d.brand));
  const isAudienceSplitBrand = AUDIENCE_SPLIT_BRANDS.includes(d.brand);
  const canSubmit = stepOneValid && stepTwoValid;

  const handleSubmit = async () => {
    if (!canSubmit || (!isEdit && !d.assigneeId)) return;
    setSubmitting(true);
    setError(null);
    try {
      let referenceFileProjectId: number = editItem?.id;
      if (isEdit) {
        // /fdc/update has no assignee support (stays read-only in this mode).
        // participant_ids/observer_ids/task_deadline_date ARE supported -
        // participant_ids as a full replacement of this project's
        // cc_fdc_project_member rows; observer_ids and task_deadline_date are
        // only sent when this user is actually allowed to change them -
        // otherwise the backend would 403 the whole save just for an
        // unchanged, merely-redisplayed value.
        await commonService.fdcUpdate({
          id: editItem.id,
          summary: d.summary,
          crm_id: d.crmId,
          deal_id: d.dealId,
          comment: d.comment || '',
          criteria_groups: d.criteriaGroups.map((g) => criteriaGroupToApi(g, d.brand)),
          project_tire: d.projectTier,
          brand_name: d.brand,
          payment_type: d.paymentType,
          record_type: d.acquisitionType,
          refulfillment_id: (d.acquisitionType === 'refulfillment' || d.acquisitionType === 'credit_fulfillment') ? d.refulfillmentId : '',
          criteria: d.listTypes.join(','),
          participant_ids: d.participantIds,
          ...(canManageObservers ? { observer_ids: d.observerIds } : {}),
          ...(isBdOwnerOrAdmin ? { task_deadline_date: d.dueDate || undefined, assignee_id: d.assigneeId || undefined } : {}),
        });
      } else {
        const formData = new FormData();
        formData.append('summary', d.summary);
        formData.append('crm_id', d.crmId);
        d.listTypes.forEach((t) => formData.append('criteria[]', t));
        formData.append('project_tire', d.projectTier);
        formData.append('brand_name', d.brand);
        formData.append('payment_type', d.paymentType);
        formData.append('record_type', d.acquisitionType);
        formData.append('criteria_groups', JSON.stringify(d.criteriaGroups.map((g) => criteriaGroupToApi(g, d.brand))));
        formData.append('assignee_id', String(d.assigneeId));
        d.participantIds.forEach((id) => formData.append('participant_ids[]', String(id)));
        d.observerIds.forEach((id) => formData.append('observer_ids[]', String(id)));
        formData.append('deal_id', d.dealId);
        if (d.dueDate) formData.append('task_deadline_date', d.dueDate);
        if ((d.acquisitionType === 'refulfillment' || d.acquisitionType === 'credit_fulfillment') && d.refulfillmentId) formData.append('refulfillment_id', d.refulfillmentId);
        if (d.comment) formData.append('comment', d.comment);

        const createRes: any = await commonService.fdcUpload(formData);
        referenceFileProjectId = createRes?.data?.id;
      }

      if (pendingReferenceFiles.length > 0 && referenceFileProjectId) {
        for (const file of pendingReferenceFiles) {
          await referenceFileService.upload(referenceFileProjectId, file);
        }
      }

      onCreated ? onCreated() : onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || `Failed to ${isEdit ? 'save' : 'create'} project`);
    } finally {
      setSubmitting(false);
    }
  };

  let stepBody: React.ReactNode = null;

  if (step === 1) {
    stepBody = (
      <div className="wf-stack">
        <div className="wf-card wf-card-pad" style={{ background: '#f8f9fa' }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em', color: '#6b7280', marginBottom: 6 }}>Project title</div>
          <div style={{ fontWeight: 400, fontSize: 13 }}>{d.summary || '…'}</div>
        </div>

        <div className="wf-card wf-card-pad" style={{ background: '#f8f9fa' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <label className="wf-field-label" style={{ margin: 0 }}>HubSpot / CRM identity</label>
            <span className="hs-badge" style={{ fontSize: 11, background: '#e8f0fe', color: '#3b82f6', padding: '3px 8px', borderRadius: 7, fontWeight: 700 }}>⬡ required</span>
          </div>
          <div className="wf-grid2">
            <Field label="Contact ID / CRM ID" required>
              <input type="text" value={d.crmId} onChange={(e) => update({ crmId: e.target.value.replace(/[^0-9]/g, '') })} placeholder="Enter CRM ID" />
            </Field>
            <Field label="Deal ID" required>
              <input type="text" value={d.dealId} onChange={(e) => update({ dealId: e.target.value })} placeholder="HubSpot Deal ID" />
            </Field>
          </div>
        </div>

        <div className="wf-grid2">
          <Field label="Fulfillment Type">
            <select value={d.acquisitionType} onChange={(e) => update({ acquisitionType: e.target.value as any })}>
              <option value="fulfillment">Fulfillment</option>
              <option value="refulfillment">Refulfillment</option>
              <option value="credit_fulfillment">Credit Fulfillment</option>
              {/* Goodwill is Customer Experience-only - not a role today (both
                  hold Business Development), so this is a hardcoded email
                  check, same as the backend's CX_USER_EMAILS gate. */}
              {isCXUser() && <option value="goodwill">Goodwill</option>}
            </select>
          </Field>
          <Field label="List type">
            <div className="wf-radiorow">
              <label><input type="checkbox" checked={d.listTypes.includes('B2B')} onChange={() => toggleListType('B2B')} /> B2B</label>
              <label><input type="checkbox" checked={d.listTypes.includes('B2C')} onChange={() => toggleListType('B2C')} /> B2C</label>
            </div>
          </Field>
        </div>

        {(d.acquisitionType === 'refulfillment' || d.acquisitionType === 'credit_fulfillment') && (
          <div className="wf-card wf-card-pad" style={{ background: '#f8f9fa' }}>
            <label className="wf-field-label">Find original project (search by CRM ID above)</label>
            {!d.crmId ? (
              <div className="wf-field-hint">Enter the Contact ID / CRM ID above first to search matching delivered projects.</div>
            ) : (
              <>
                <select value={d.refulfillmentId} onChange={(e) => update({ refulfillmentId: e.target.value })}>
                  <option value="">-- Select original project --</option>
                  {refulfillOptions.map((o) => (
                    <option key={o.projectid} value={o.projectid}>{o.projectid} ({o.summary})</option>
                  ))}
                </select>
                {refulfillOptions.length === 0 && (
                  <div className="wf-field-hint">No delivered projects match this CRM ID yet.</div>
                )}
              </>
            )}
          </div>
        )}

        <div className="wf-grid2">
          <Field label="Project tier">
            <select value={d.projectTier} onChange={(e) => update({ projectTier: e.target.value })}>
              <option value="T1">T1</option>
              <option value="T2">T2</option>
            </select>
          </Field>
          <Field
            label="Requested delivery date"
            hint={isEdit && isBdOwnerOrAdmin ? "Every subtask's own due date is capped to this - moving it earlier won't touch subtasks already past it." : undefined}
          >
            {isEdit && !isBdOwnerOrAdmin ? (
              <input type="text" value={d.dueDate || '—'} disabled />
            ) : (
              <input
                type="date"
                min={todayStr}
                value={d.dueDate}
                onChange={(e) => {
                  if (e.target.value && e.target.value < todayStr) return;
                  update({ dueDate: e.target.value });
                }}
              />
            )}
          </Field>
        </div>

        <div className="wf-grid2">
          <Field label="Brand" required>
            <select value={d.brand} onChange={(e) => update({ brand: e.target.value })}>
              <option value="">Select brand...</option>
              {BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </Field>
          <Field label="Payment Type" required={!isEdit}>
            <select value={d.paymentType} onChange={(e) => update({ paymentType: e.target.value })}>
              <option value="">Select payment type...</option>
              <option value="CC">CC</option>
              <option value="Non-CC">Non-CC</option>
            </select>
          </Field>
        </div>

        <Field
          label="Assignee"
          required={!isEdit}
          hint={
            isEdit
              ? (isBdOwnerOrAdmin ? 'Reassigning hands the whole project to a different PC Manager - existing subtasks keep their own assignees.' : undefined)
              : 'Whoever this project is handed off to — kept separate from Participants and Observers.'
          }
        >
          {isEdit && !isBdOwnerOrAdmin ? (
            <input type="text" value={editItem.assignee_name || 'Unassigned'} disabled />
          ) : (
            <UserPicker
              role="pc_manager"
              value={d.assigneeId}
              initialUser={assigneeUser}
              onChange={(id, user) => { update({ assigneeId: id }); setAssigneeUser(user); }}
              placeholder="Type a PC Manager's name..."
            />
          )}
        </Field>
      </div>
    );
  } else if (step === 2) {
    stepBody = (
      <div className="wf-stack">
        {d.criteriaGroups.map((group, idx) => {
          const complete = isCriteriaGroupComplete(group, d.brand);
          const subtitleParts = isAudienceSplitBrand
            ? ([
                group.includeAttendees && group.attendees.primaryIndustry && `Attendees: ${group.attendees.primaryIndustry}`,
                group.includeExhibitors && group.exhibitors.primaryIndustry && `Exhibitors: ${group.exhibitors.primaryIndustry}`,
              ].filter(Boolean) as string[])
            : [group.primaryIndustry, group.specialty, group.jobTitles, group.geography].filter(Boolean);
          const contactCount = groupContactCount(group, d.brand);
          return (
            <div key={idx} className="wf-criteria-summary-row">
              <div className="wf-criteria-summary-main" onClick={() => openEditCriteria(idx)}>
                <div className="wf-criteria-summary-title">
                  Criteria {idx + 1}
                  <span className="wf-criteria-summary-contacts">{contactCount ? `${contactCount} contacts` : '—'}</span>
                  {!complete && <span className="wf-chip2 wf-chip2-warn"><span className="wf-chip2-dot" />Incomplete</span>}
                </div>
                <div className="wf-criteria-summary-sub">
                  {subtitleParts.length > 0 ? subtitleParts.join(' · ') : 'Click to fill in industry, specialty, job titles, geography...'}
                </div>
              </div>
              <div className="wf-criteria-summary-actions">
                <button type="button" className="wf-btn wf-btn-sm" onClick={() => openEditCriteria(idx)}>✏️ Edit</button>
                {d.criteriaGroups.length > 1 && (
                  <button type="button" className="wf-file-menu-trigger" onClick={() => removeCriteriaGroup(idx)} title="Remove this criteria">
                    ✕
                  </button>
                )}
              </div>
            </div>
          );
        })}
        <button type="button" className="wf-btn" style={{ alignSelf: 'flex-start' }} onClick={openAddCriteria}>
          + Add Criteria
        </button>
        <Field label="Reference / Files" hint="Any type of file — sample file, enrichment source, work file, anything. Uploaded once you save.">
          <label className="wf-btn" style={{ cursor: 'pointer', display: 'inline-block' }}>
            + Add file(s)
            <input
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                // e.target.files is a live FileList tied to the input - it
                // gets cleared as soon as e.target.value is reset below, so
                // it must be copied into a plain array *before* that, not
                // read lazily inside the setState updater (which React
                // invokes later, by which point the live list is empty).
                const selected = Array.from(e.target.files || []);
                if (selected.length > 0) setPendingReferenceFiles((prev) => [...prev, ...selected]);
                e.target.value = '';
              }}
            />
          </label>
          {pendingReferenceFiles.length > 0 && (
            <div className="wf-card wf-card-pad" style={{ marginTop: 10 }}>
              {pendingReferenceFiles.map((file, idx) => (
                <div key={`${file.name}-${idx}`} className="wf-filerow">
                  <span>📎</span>
                  <span className="wf-fname" title={file.name}>{file.name}</span>
                  <button
                    type="button"
                    className="wf-file-menu-trigger"
                    onClick={() => setPendingReferenceFiles((prev) => prev.filter((_, i) => i !== idx))}
                    title="Remove"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </Field>
      </div>
    );
  } else if (step === 3) {
    stepBody = (
      <div className="wf-stack">
        <Field label="Comment (internal)">
          <textarea rows={6} value={d.comment} onChange={(e) => update({ comment: e.target.value })} placeholder="Anything the PC Manager should know..." />
        </Field>
      </div>
    );
  } else {
    stepBody = (
      <div className="wf-stack">
        <div className="wf-card wf-card-pad" style={{ background: '#f8f9fa' }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em', color: '#6b7280', marginBottom: 8 }}>Project title</div>
          <div style={{ fontWeight: 400, fontSize: 13 }}>{d.summary || '…'}</div>
        </div>
        <div className="wf-card wf-card-pad" style={{ background: '#f8f9fa' }}>
          <div className="wf-field-label" style={{ marginBottom: 8 }}>Criteria ({d.criteriaGroups.length})</div>
          <div className="wf-stack" style={{ gap: 10 }}>
            {d.criteriaGroups.map((g, idx) => (
              <div key={idx} style={{ fontSize: 13 }}>
                <span style={{ fontWeight: 700, color: '#6b7280' }}>Criteria {idx + 1}</span>
                {isAudienceSplitBrand ? (
                  <>
                    {g.includeAttendees && (
                      <div style={{ marginTop: 6 }}>
                        <span className="wf-k" style={{ fontWeight: 700 }}>Attendees</span>
                        <CriteriaReadOnlyGrid values={g.attendees} labelPrefix="Attendees" />
                      </div>
                    )}
                    {g.includeExhibitors && (
                      <div style={{ marginTop: 6 }}>
                        <span className="wf-k" style={{ fontWeight: 700 }}>Exhibitors</span>
                        <CriteriaReadOnlyGrid values={g.exhibitors} labelPrefix="Exhibitors" />
                      </div>
                    )}
                  </>
                ) : (
                  <CriteriaReadOnlyGrid values={g} />
                )}
                {g.bdAdditionalNotes && (
                  <div style={{ marginTop: 6 }}>
                    <span className="wf-k">BD Additional Notes</span>
                    <div style={{ fontSize: 13, marginTop: 2 }} dangerouslySetInnerHTML={{ __html: g.bdAdditionalNotes }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <Field label="Participants" hint="Anyone else who should have full access to work on this project.">
          <UserMultiPicker value={d.participantIds} onChange={(ids) => update({ participantIds: ids })} placeholder="Add participants..." />
        </Field>
        <Field
          label="Observers"
          hint={canManageObservers
            ? 'Read-only visibility into everything on this project, regardless of their role.'
            : "Only an admin or this project's creating BD user can manage observers."}
        >
          {canManageObservers ? (
            <UserMultiPicker value={d.observerIds} onChange={(ids) => update({ observerIds: ids })} placeholder="Add observers..." />
          ) : (
            <div className="wf-chip-row">
              {readOnlyObservers.length ? (
                readOnlyObservers.map((m) => <span key={m.user_id} className="wf-chip">{m.name || `User #${m.user_id}`}</span>)
              ) : (
                <span className="wf-empty-hint" style={{ padding: 0 }}>None</span>
              )}
            </div>
          )}
        </Field>
      </div>
    );
  }

  return (
    <>
    <div className="wf-overlay">
      <div className="wf-modal wide">
        <div className="wf-modal-head">
          <h2 style={{ fontSize: 17 }}>{isEdit ? 'Edit FDC project' : 'New FDC project'}</h2>
          <button className="wf-iconbtn" onClick={onClose}>✕</button>
        </div>
        <div className="wf-split" style={{ minHeight: 'auto' }}>
          <div className="wf-split-left wf-form" style={{ maxHeight: 'none', flex: 1.4 }}>
            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 14 }}>Step {step} of 4 — {STEPS[step - 1]}</div>
            {error && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{error}</div>}
            {stepBody}
          </div>
          <div className="wf-split-right" style={{ maxHeight: 'none', padding: 22, borderLeft: '1px solid #e5e7eb' }}>
            <div className="wf-field-label" style={{ marginBottom: 10 }}>Progress</div>
            <div className="wf-wizard-progress">
              {STEPS.map((s, i) => (
                <div key={s} className={`wf-wizard-step-item ${i + 1 === step ? 'active' : i + 1 < step ? 'past' : ''}`}>
                  <span className="dot" />{s}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="wf-modal-foot">
          <button className="wf-btn" disabled={step === 1} onClick={() => setStep((s) => Math.max(1, s - 1))}>Back</button>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="wf-btn" onClick={onClose}>Cancel</button>
            {step < 4 ? (
              <button
                className="wf-btn wf-btn-primary"
                disabled={(step === 1 && !stepOneValid) || (step === 2 && !stepTwoValid)}
                onClick={() => setStep((s) => Math.min(4, s + 1))}
              >
                Next
              </button>
            ) : (
              <button className="wf-btn wf-btn-primary" disabled={!canSubmit || submitting} onClick={handleSubmit}>
                {submitting ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save changes' : 'Create project')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
    {criteriaPopup && (
      <div className="wf-overlay wf-overlay-nested">
        <div className="wf-modal">
          <div className="wf-modal-head">
            <h2 style={{ fontSize: 16 }}>{criteriaPopup.index === null ? 'Add criteria' : `Edit criteria ${criteriaPopup.index + 1}`}</h2>
            <button className="wf-iconbtn" onClick={closeCriteriaPopup}>✕</button>
          </div>
          <div className="wf-modal-body wf-form">
            <div className="wf-stack">
              {isAudienceSplitBrand && (
                <div className="wf-card wf-card-pad" style={{ background: '#f8f9fa' }}>
                  <div className="wf-field-label" style={{ marginBottom: 8 }}>Audience</div>
                  <div className="wf-radiorow">
                    <label>
                      <input
                        type="checkbox"
                        checked={criteriaPopup.draft.includeAttendees}
                        onChange={(e) => updateCriteriaPopupDraft({ includeAttendees: e.target.checked })}
                      /> Attendees
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={criteriaPopup.draft.includeExhibitors}
                        onChange={(e) => updateCriteriaPopupDraft({ includeExhibitors: e.target.checked })}
                      /> Exhibitors
                    </label>
                  </div>
                  {!criteriaPopup.draft.includeAttendees && !criteriaPopup.draft.includeExhibitors && (
                    <div className="wf-field-hint" style={{ color: '#ef4444', marginTop: 6 }}>
                      Select at least one of Attendees or Exhibitors.
                    </div>
                  )}
                </div>
              )}

              {isAudienceSplitBrand ? (
                <>
                  {criteriaPopup.draft.includeAttendees && (
                    <div className="wf-card wf-card-pad" style={{ background: '#f8fbff', border: '1px solid #dbeafe' }}>
                      <div className="wf-field-label" style={{ marginBottom: 8, color: '#1d4ed8' }}>Attendees</div>
                      <CriteriaFieldsBlock
                        values={criteriaPopup.draft.attendees}
                        onChange={(patch) => updateCriteriaPopupDraft({ attendees: { ...criteriaPopup.draft.attendees, ...patch } })}
                        labelPrefix="Attendees"
                      />
                    </div>
                  )}
                  {criteriaPopup.draft.includeExhibitors && (
                    <div className="wf-card wf-card-pad" style={{ background: '#fffaf5', border: '1px solid #fde8d7' }}>
                      <div className="wf-field-label" style={{ marginBottom: 8, color: '#c2410c' }}>Exhibitors</div>
                      <CriteriaFieldsBlock
                        values={criteriaPopup.draft.exhibitors}
                        onChange={(patch) => updateCriteriaPopupDraft({ exhibitors: { ...criteriaPopup.draft.exhibitors, ...patch } })}
                        labelPrefix="Exhibitors"
                      />
                    </div>
                  )}
                </>
              ) : (
                <CriteriaFieldsBlock values={criteriaPopup.draft} onChange={updateCriteriaPopupDraft} />
              )}

              <Field label="BD Additional Notes" required>
                <div className="wf-requirements-editor">
                  <CKEditor
                    editor={ClassicEditor as any}
                    data={criteriaPopup.draft.bdAdditionalNotes || ''}
                    onChange={(_, editor) => updateCriteriaPopupDraft({ bdAdditionalNotes: editor.getData() })}
                    config={{ placeholder: 'Enter additional notes' }}
                  />
                </div>
              </Field>
            </div>
          </div>
          <div className="wf-modal-foot">
            <button className="wf-btn" onClick={closeCriteriaPopup}>Cancel</button>
            <button className="wf-btn wf-btn-primary" disabled={!criteriaPopupValid} onClick={saveCriteriaPopup}>
              {criteriaPopup.index === null ? 'Add criteria' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default FDCCreateWizard;
